import { usePersistence } from "../../../hooks";
import { ContactDetailsEventArgs, RememberedContactDetails } from "../../../types";
import { UseContactDetailsValidationReturn } from "./useContactDetailsValidation";
import { useSpeciesAndBreeds } from "./useSpeciesAndBreeds";

interface UseContactDetailsPersistenceProps {
  rememberDetailsChecked: boolean;
  isRestoring: boolean;
  validation: UseContactDetailsValidationReturn;
  speciesAndBreeds: ReturnType<typeof useSpeciesAndBreeds>;
  existingContactDetails?: ContactDetailsEventArgs;
  saveContactDetails: (details: RememberedContactDetails) => void;
  removeContactDetails: () => void;
}

/**
 * Hook to handle debounced persistence of contact details to localStorage.
 * Automatically saves or removes data based on the remember checkbox state.
 *
 * This hook is a specialized wrapper around the generic `usePersistence` hook,
 * configured specifically for contact details persistence.
 *
 * @param props Configuration including validation state and storage operations
 *
 * @example
 * ```tsx
 * useContactDetailsPersistence({
 *   rememberDetailsChecked: true,
 *   isRestoring: false,
 *   validation: validationHook,
 *   speciesAndBreeds: speciesAndBreedsHook,
 *   saveContactDetails: save,
 *   removeContactDetails: remove
 * });
 * ```
 */
export function useContactDetailsPersistence({
  rememberDetailsChecked,
  isRestoring,
  validation,
  speciesAndBreeds,
  existingContactDetails,
  saveContactDetails,
  removeContactDetails
}: UseContactDetailsPersistenceProps) {
  // Prepare the data structure to persist
  const dataToSave: RememberedContactDetails = {
    rememberedDetailsChecked: true,
    client: {
      identificationNumber: validation.client.identificationNumber,
      firstName: validation.client.firstName,
      lastName: validation.client.lastName,
      email: existingContactDetails?.client?.email,
      phoneNumber: existingContactDetails?.client?.phoneNumber,
      streetAddress: validation.client.streetAddress,
      zipCode: validation.client.zipCode,
      city: validation.client.city
    },
    patient: {
      name: validation.patient.name,
      notes: validation.patient.notes,
      species: speciesAndBreeds.selectedSpecies,
      breed: speciesAndBreeds.selectedBreed
    }
  };

  // Use the generic persistence hook
  usePersistence({
    key: "provetContactDetails",
    value: dataToSave,
    shouldPersist: rememberDetailsChecked,
    isRestoring,
    debounceMs: 400,
    onSave: saveContactDetails,
    onRemove: removeContactDetails
  });
}
